'use client';

import { swal } from '@/lib/sweetalert';
import { pickErrorMessages } from '@/utils/functions';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type MaterialCategoryNameForApi, getMaterialsByType } from '../api/getMaterialListByTypeInfinite';
import type { GfMaterialItem } from '../types';

/** 前後どちらの端に近づいたら次ページを先読みするかの余白（この件数以内に入ったら読む） */
const PREFETCH_THRESHOLD = 2;

export type MaterialSequenceApiParams = {
  workId: number;
  categoryName: MaterialCategoryNameForApi;
  limit: number;
  tagIds?: number[];
  gfProductId?: number;
};

/** モーダルを開いた時点の初期状態。グリッドから開いたら読み込み済みの一覧、共有URL・直開きなら当該コマ 1 件だけを渡す。 */
export type MaterialSequenceInitialState = {
  items: GfMaterialItem[];
  index: number;
  /** items の先頭より前にまだコマがあるか（グリッドは先頭から読むので false、共有URL・直開きは true） */
  hasPrev: boolean;
  /** items の末尾より後にまだコマがあるか */
  hasNext: boolean;
};

type MaterialSequenceResult = {
  items: GfMaterialItem[];
  currentIndex: number;
  isPrevDisabled: boolean;
  isNextDisabled: boolean;
  /** 先頭側へ読み足して index がずれたとき、Splide を新しい開始位置から作り直すためのキー */
  sliderKey: number;
  /** スワイプ/矢印で表示スライドが変わったときに呼ぶ。端に近づいたらその向きを先読みする */
  handleIndexChange: (index: number) => void;
  /** 並び内のコマのお気に入り状態を即時反映する */
  updateFavoriteInCache: (materialId: number, isFavorite: boolean) => void;
};

/**
 * コマ詳細モーダルの前後送りを管理する。
 * グリッド用の useMaterialLoader とは分けて、開いたコマから前後どちらへも読み足せる 1 本の並びを、
 * 双方向カーソル（getMaterialsByType の direction）で持つ。先頭側へ読み足すと現在位置の index がずれるため、
 * その時だけ sliderKey を更新して Splide を新しい開始位置から作り直す（末尾側への追加は作り直し不要）。
 */
export function useMaterialSequence(
  initialState: MaterialSequenceInitialState,
  apiParams?: MaterialSequenceApiParams,
): MaterialSequenceResult {
  const [items, setItems] = useState<GfMaterialItem[]>(initialState.items);
  const [currentIndex, setCurrentIndex] = useState(initialState.index);
  const [hasPrev, setHasPrev] = useState(initialState.hasPrev);
  const [hasNext, setHasNext] = useState(initialState.hasNext);
  const [sliderKey, setSliderKey] = useState(0);
  // 連続スワイプ中の多重フェッチを同期的に防ぐ
  const isLoadingPrevRef = useRef(false);
  const isLoadingNextRef = useRef(false);

  const loadNext = useCallback(async () => {
    if (!apiParams || isLoadingNextRef.current) return;
    isLoadingNextRef.current = true;
    try {
      const lastId = items[items.length - 1]?.id;
      if (lastId === undefined) return;
      const { result } = await getMaterialsByType({
        gfWorkId: apiParams.workId,
        categoryName: apiParams.categoryName,
        limit: apiParams.limit,
        direction: 'next',
        lastMaterialId: lastId,
        tagIds: apiParams.tagIds,
        gfProductId: apiParams.gfProductId,
      });
      const category = result.categories.find((c) => c.categoryName === apiParams.categoryName) ?? result.categories[0];
      if (!category || category.materials.length === 0) {
        setHasNext(false);
        return;
      }
      // 末尾への追加は現在位置の index を動かさないので Splide の作り直しは不要
      setItems((prev) => [...prev, ...category.materials]);
      setHasNext(category.hasMore);
    } catch (error) {
      void swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      isLoadingNextRef.current = false;
    }
  }, [apiParams, items]);

  const loadPrev = useCallback(async () => {
    if (!apiParams || isLoadingPrevRef.current) return;
    isLoadingPrevRef.current = true;
    try {
      const firstId = items[0]?.id;
      if (firstId === undefined) return;
      const { result } = await getMaterialsByType({
        gfWorkId: apiParams.workId,
        categoryName: apiParams.categoryName,
        limit: apiParams.limit,
        direction: 'prev',
        lastMaterialId: firstId,
        tagIds: apiParams.tagIds,
        gfProductId: apiParams.gfProductId,
      });
      const category = result.categories.find((c) => c.categoryName === apiParams.categoryName) ?? result.categories[0];
      if (!category || category.materials.length === 0) {
        setHasPrev(false);
        return;
      }
      // 先頭への追加は現在位置を後ろへずらすため、追加数ぶん index を補正し Splide を作り直す
      setItems((prev) => [...category.materials, ...prev]);
      setCurrentIndex((prev) => prev + category.materials.length);
      setHasPrev(category.hasMore);
      setSliderKey((prev) => prev + 1);
    } catch (error) {
      void swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      isLoadingPrevRef.current = false;
    }
  }, [apiParams, items]);

  // 表示位置が端に近ければ、その向きの次ページを先読みする
  const prefetchAroundEdges = useCallback(
    (index: number) => {
      if (hasNext && index >= items.length - 1 - PREFETCH_THRESHOLD) void loadNext();
      if (hasPrev && index <= PREFETCH_THRESHOLD) void loadPrev();
    },
    [hasNext, hasPrev, items.length, loadNext, loadPrev],
  );

  const handleIndexChange = useCallback(
    (index: number) => {
      setCurrentIndex(index);
      prefetchAroundEdges(index);
    },
    [prefetchAroundEdges],
  );

  // 開いた直後にも端なら先読みする。単体起動（共有URL・直開き）は 1 枚のままだとスワイプが起きず
  // onMove 経由の先読みが走らないため、ここで最初の前後を読み込んでおく。
  useEffect(() => {
    prefetchAroundEdges(currentIndex);
    // マウント時に 1 回だけ（以降の先読みは handleIndexChange 側で行う）
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFavoriteInCache = useCallback((materialId: number, isFavorite: boolean) => {
    setItems((prev) => {
      if (!prev.some((m) => m.id === materialId)) return prev;
      return prev.map((m) => (m.id === materialId ? { ...m, isFavorite } : m));
    });
  }, []);

  return {
    items,
    currentIndex,
    // 読み足しが終わるまでは端から動かせない。hasPrev/hasNext で活性にすると、押しても何も起きない
    // 矢印になるため、いま動けるか（index が端か）で判定する
    isPrevDisabled: currentIndex <= 0,
    isNextDisabled: currentIndex >= items.length - 1,
    sliderKey,
    handleIndexChange,
    updateFavoriteInCache,
  };
}
